import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import loadable from '@loadable/component';

import { bool, object } from 'prop-types';
import { compose } from 'redux';
import { connect } from 'react-redux';

// import { camelize } from '../../util/string';
import { propTypes } from '../../util/types';

// import FallbackPage from './FallbackPage';
// import { ASSET_NAME } from './LandingPage.duck';
import {
  AvatarMedium,
  H4,
  InlineTextButton,
  ListingCard,
  NamedLink,
  Page,
  PrimaryButton,
  ResponsiveImage,
} from '../../components';
import classNames from 'classnames';
import TopbarContainer from '../TopbarContainer/TopbarContainer';
import { FormattedMessage, useIntl } from 'react-intl';
import { useConfiguration } from '../../context/configurationContext';
import FooterComponent from '../FooterContainer/FooterContainer';
import css from './LandingPage.module.css';
import SectionContainer from '../PageBuilder/SectionBuilder/SectionContainer';

import whyKlosetIcon1 from '../../assets/svg/whyKlosetIcon1.svg';
import whyKlosetIcon2 from '../../assets/svg/whyKlosetIcon2.svg';
import whyKlosetIcon3 from '../../assets/svg/whyKlosetIcon3.svg';
import whyKlosetIcon4 from '../../assets/svg/whyKlosetIcon4.svg';

import howImg1 from '../../assets/svg/howImg1.svg';
import howImg2 from '../../assets/svg/howImg2.svg';
import howImg3 from '../../assets/svg/howImg3.svg';
import howImg4 from '../../assets/svg/howImg4.svg';
// import { getListingsById } from '../../ducks/marketplaceData.duck';
import { withRouter } from 'react-router-dom/cjs/react-router-dom';
import { useRouteConfiguration } from '../../context/routeConfigurationContext';
import { createResourceLocatorString } from '../../util/routes';
import { FaStar } from 'react-icons/fa';
import { MdKeyboardArrowRight } from 'react-icons/md';
import { FaChevronLeft, FaChevronRight } from 'react-icons/fa6';

const SlickSlider = loadable(() => import('react-slick'));

let slickPreloadPromise;
let slickReady = false;
const slickReadyCallbacks = [];

const preloadSlickOnce = () => {
  if (!slickPreloadPromise) {
    slickPreloadPromise = Promise.resolve(SlickSlider.preload()).then(() => {
      slickReady = true;
      slickReadyCallbacks.forEach(cb => cb());
      slickReadyCallbacks.length = 0;
    });
  }
  return slickPreloadPromise;
};

const Carousel = props => {
  const { children, className, ...sliderProps } = props;
  const [ready, setReady] = useState(slickReady);

  useEffect(() => {
    if (slickReady) {
      setReady(true);
      return;
    }

    const callback = () => setReady(true);
    slickReadyCallbacks.push(callback);
    preloadSlickOnce();

    return () => {
      const idx = slickReadyCallbacks.indexOf(callback);
      if (idx > -1) slickReadyCallbacks.splice(idx, 1);
    };
  }, []);

  return ready ? (
    <SlickSlider className={className} {...sliderProps}>
      {children}
    </SlickSlider>
  ) : (
    <div className={classNames(css.carouselFallback, className)}>{children}</div>
  );
};

const useInViewOnce = (options = {}) => {
  const { rootMargin = '200px', threshold = 0, disabled = false } = options;
  const ref = useRef(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    if (disabled) {
      if (!isVisible) setIsVisible(true);
      return;
    }
    if (isVisible) return;
    const element = ref.current;
    if (!element) return;

    if (typeof IntersectionObserver === 'undefined') {
      setIsVisible(true);
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { rootMargin, threshold }
    );

    observer.observe(element);
    return () => observer.disconnect();
  }, [disabled, isVisible, rootMargin, threshold]);

  return [ref, isVisible];
};

const RenderWhenVisible = props => {
  const { children, rootMargin = '200px', minHeight, disabled = false } = props;
  const [ref, isVisible] = useInViewOnce({ rootMargin, disabled });

  return (
    <div ref={ref} style={minHeight ? { minHeight } : null}>
      {isVisible ? children : null}
    </div>
  );
};

// import lux from '../../assets/temp/lux.png';
// import formal from '../../assets/temp/formal.png';
// import evening from '../../assets/temp/evening_fits.png';
import sundresses from '../../assets/temp/sundresses.webp';
import streetkicks from '../../assets/temp/streetkicks.webp';
import carryalls from '../../assets/temp/carryalls.webp';
import goodjeans from '../../assets/temp/goodjeans.webp';
import summertops from '../../assets/temp/summertops.webp';
import hotheels from '../../assets/temp/hotheels.webp';
import { addToLikeList } from '../../ducks/user.duck';
import testimonialData from '../../assets/json/klub_reviews.json';
import Field from '../PageBuilder/Field';

const HERO_IMAGE_URL = 'https://assets.klosetklub.com/newBannerImg.webp';

const WHY_KLOSET_ICONS = [whyKlosetIcon1, whyKlosetIcon2, whyKlosetIcon3, whyKlosetIcon4];
const HOW_IT_WORKS_ICONS = [howImg1, howImg2, howImg3, howImg4];

const SHOP_BY_VIBE = [
  {
    title: 'Sun Dresses',
    img: sundresses,
    paramKey: 'sunDressesVibeListing',
    alt: 'Sun Dresses Image',
  },
  {
    title: 'Street Kicks',
    img: streetkicks,
    paramKey: 'streetKicksVibeListing',
    alt: 'Street Kicks Image',
  },
  {
    title: 'Carry Alls',
    img: carryalls,
    paramKey: 'carryAllsVibeListing',
    alt: 'Carry Alls Image',
  },
  {
    title: 'Good Jeans',
    img: goodjeans,
    paramKey: 'goodJeansVibeListing',
    alt: 'Good Jeans Image',
  },
  {
    title: 'Summer Tops',
    img: summertops,
    paramKey: 'summerTopsVibeListing',
    alt: 'Summer Tops Image',
  },
  {
    title: 'Hot Heels',
    img: hotheels,
    paramKey: 'hotHeelsVibeListing',
    alt: 'Hot Heels Image',
  },
];

const NextArrow = React.memo(props => {
  const { className, style, onClick } = props;
  return (
    <div
      className={className}
      style={{
        ...style,
        zIndex: 5,
      }}
      onClick={onClick}
    >
      <FaChevronRight />
    </div>
  );
});

const PrevArrow = React.memo(props => {
  const { className, style, onClick } = props;
  return (
    <div
      className={className}
      style={{
        ...style,
        zIndex: 5,
      }}
      onClick={onClick}
    >
      <FaChevronLeft />
    </div>
  );
});

// const PageBuilder = loadable(() =>
//   import(/* webpackChunkName: "PageBuilder" */ '../PageBuilder/PageBuilder')
// );

function DesignedImagerWrapper(props) {
  const { intl, firstBtn, secondBtn, onSubmitFirst, onSubmitSecond, title, heroData } = props;

  const imageUrl = heroData?.appearance?.backgroundImage;

  const variants = imageUrl?.attributes?.variants || {};

  return (
    <div className={css.imageWrapper}>
      <ResponsiveImage
        rootClassName={css.rootForImage}
        alt={'Kloset Klub - Rent and Lend Fashion'}
        image={imageUrl}
        variants={variants ? Object.keys(variants) : []}
      />
      {/* <img
        src={HERO_IMAGE_URL}
        fetchpriority="high"
        loading="eager"
        decoding="async"
        alt="Kloset Klub - Rent and Lend Fashion"
        width="1920"
        height="800"
      /> */}
      <div className={css.headerContentContainer}>
        <div className={css.headerContainer}>
          {/* {title && <H4 as="h2">{intl.formatMessage({ id: title })}</H4>} */}
          {/* {title && <H4 as="h2">{intl.formatMessage({ id: title })}</H4>} */}
          <div className={css.ctaContainer}>
            {/* <PrimaryButton
              onClick={() => onSubmitFirst('CreateListingPage')}
              className={css.headingBtn}
            >
              {intl.formatMessage({ id: firstBtn })}
            </PrimaryButton> */}
            <PrimaryButton onClick={() => onSubmitSecond('SearchPage')} className={css.headingBtn}>
              {intl.formatMessage({ id: secondBtn })}
            </PrimaryButton>
          </div>
        </div>
      </div>
    </div>
  );
}

export const LandingPageComponent = props => {
  const {
    listings,
    filteredListings,
    history,
    topBrands,
    klubPicks,
    favroiteListingIds,
    onAddList,
    pageAssetsData,
  } = props;

  const config = useConfiguration();

  const landingPageAssetData = pageAssetsData?.landingPage?.data || {};
  const sections = landingPageAssetData?.sections || [];

  const heroData = sections.find(s => s?.sectionType === 'hero');
  const description = heroData?.description?.content;

  const routeConfiguration = useRouteConfiguration();
  const intl = useIntl();
  const brandsRef = useRef(null);
  // const { routeName, pathParams } = getSearchPageResourceLocatorStringParams(
  //   routeConfiguration,
  //   location
  // );

  const schemaTitleVars = {
    marketplaceName: config.marketplaceName,
    tagLine: intl.formatMessage({ id: 'LandingPage.metaTagLine' }),
  };
  const schemaTitle = intl.formatMessage({ id: 'LandingPage.metaTitle' }, schemaTitleVars);

  // Avoid reading window during render (SSR/hydration + perf).
  const [viewport, setViewport] = useState('desktop');
  useEffect(() => {
    if (typeof window === 'undefined') return;

    let rafId = null;
    const computeViewport = width => {
      if (width < 768) return 'mobile';
      if (width < 992) return 'tablet';
      return 'desktop';
    };

    const onResize = () => {
      if (rafId) return;
      rafId = window.requestAnimationFrame(() => {
        rafId = null;
        setViewport(computeViewport(window.innerWidth));
      });
    };

    setViewport(computeViewport(window.innerWidth));
    window.addEventListener('resize', onResize, { passive: true });
    return () => {
      if (rafId) window.cancelAnimationFrame(rafId);
      window.removeEventListener('resize', onResize);
    };
  }, []);

  const nextArrowMemo = useMemo(() => <NextArrow />, []);
  const prevArrowMemo = useMemo(() => <PrevArrow />, []);

  const createSettings = useCallback(
    ({ mobile, tablet, desktop }) => {
      const initial = viewport === 'mobile' ? mobile : viewport === 'tablet' ? tablet : desktop;
      const showArrows = viewport === 'desktop';

      return {
        lazyLoad: 'ondemand',
        infinite: false,
        speed: 500,
        swipeToSlide: true,
        nextArrow: nextArrowMemo,
        prevArrow: prevArrowMemo,
        arrows: showArrows,
        slidesToShow: initial.slidesToShow,
        slidesToScroll: initial.slidesToScroll,
        responsive: [
          {
            breakpoint: 767,
            settings: { ...mobile, arrows: false },
          },
          {
            breakpoint: 991,
            settings: { ...tablet, arrows: false },
          },
          {
            breakpoint: 9999,
            settings: { ...desktop, arrows: true },
          },
        ],
      };
    },
    [viewport, nextArrowMemo, prevArrowMemo]
  );

  const settings = useMemo(
    () =>
      createSettings({
        mobile: { slidesToShow: 2.2, slidesToScroll: 1 },
        tablet: { slidesToShow: 3.2, slidesToScroll: 2 },
        desktop: { slidesToShow: 4.2, slidesToScroll: 4 },
      }),
    [createSettings]
  );

  const klubPicksSettings = useMemo(
    () =>
      createSettings({
        mobile: { slidesToShow: 1.2, slidesToScroll: 1 },
        tablet: { slidesToShow: 2.2, slidesToScroll: 2 },
        desktop: { slidesToShow: 3.2, slidesToScroll: 2 },
      }),
    [createSettings]
  );

  const reviewSettings = useMemo(
    () =>
      createSettings({
        mobile: { slidesToShow: 1.4, slidesToScroll: 1 },
        tablet: { slidesToShow: 1.4, slidesToScroll: 1 },
        desktop: { slidesToShow: 3.2, slidesToScroll: 2 },
      }),
    [createSettings]
  );

  const vibeSettings = useMemo(
    () =>
      createSettings({
        mobile: { slidesToShow: 2.2, slidesToScroll: 1 },
        tablet: { slidesToShow: 2.2, slidesToScroll: 1 },
        desktop: { slidesToShow: 4.3, slidesToScroll: 3 },
      }),
    [createSettings]
  );

  const brandsSettings = useMemo(
    () =>
      createSettings({
        mobile: { slidesToShow: 5.5, slidesToScroll: 1 },
        tablet: { slidesToShow: 7.2, slidesToScroll: 3 },
        desktop: { slidesToShow: 8.3, slidesToScroll: 8 },
      }),
    [createSettings]
  );

  const draftId = '00000000-0000-0000-0000-000000000000';
  const createListingLinkConfig = useMemo(
    () => ({
      group: 'primary',
      text: intl.formatMessage({ id: 'TopbarDesktop.createListing' }),
      type: 'internal',
      route: {
        name: 'EditListingPage',
        params: { slug: 'draft', id: draftId, type: 'new', tab: 'details' },
      },
      highlight: true,
    }),
    [intl]
  );

  const [forceBrandsVisible, setForceBrandsVisible] = useState(false);
  const [hotRef, showHot] = useInViewOnce({ rootMargin: '250px' });

  const handleRedirect = useCallback(
    page => {
      if (page === 'SearchPage') {
        history.push(createResourceLocatorString('SearchPage', routeConfiguration, {}, {}));
      } else if (page === 'CreateListingPage') {
        const {
          route: { name, params },
        } = createListingLinkConfig;
        history.push(createResourceLocatorString(name, routeConfiguration, params));
      }
    },
    [createListingLinkConfig, history, routeConfiguration]
  );

  const handleBrandClick = useCallback(
    brandValue => {
      history.push(
        createResourceLocatorString(
          'BrandPage',
          routeConfiguration,
          { brandSlug: brandValue.toLowerCase() },
          {}
        )
      );
    },
    [history, routeConfiguration]
  );

  const handleVibeClick = useCallback(
    paramKey => {
      history.push(
        createResourceLocatorString(
          'SearchPage',
          routeConfiguration,
          {},
          { [`meta_${paramKey}`]: true }
        )
      );
    },
    [history, routeConfiguration]
  );

  const handleCategoryClick = useCallback(
    categorySlug => {
      history.push(
        createResourceLocatorString('CategoryPage', routeConfiguration, { categorySlug }, {})
      );
    },
    [history, routeConfiguration]
  );

  const ShowMoreRedirectSearchButton = React.memo(props => {
    const { params = {}, extra } = props;

    // const handleRedirect = () => {
    //   history.push({
    //     pathname: createResourceLocatorString('SearchPage', routeConfiguration, {}, params),
    //     ...extra,
    //   });
    // };

    return (
      <InlineTextButton
        onClick={() =>
          extra
            ? history.push({
                pathname: createResourceLocatorString('SearchPage', routeConfiguration, {}, params),
                ...extra,
              })
            : history.push(
                createResourceLocatorString('SearchPage', routeConfiguration, {}, params)
              )
        }
      >
        <FormattedMessage id="LandingPage.brandsCta" />
        <MdKeyboardArrowRight />
      </InlineTextButton>
    );
  });

  const Empty = React.memo(({ textId }) => {
    return (
      <div className={css.emptyDiv}>
        <FormattedMessage id={textId} />
      </div>
    );
  });

  // console.log(3434, config);

  const scrollToBrands = useCallback(() => {
    // Brands section is lazy-rendered; force it visible and then scroll once it mounts.
    setForceBrandsVisible(true);

    const tryScroll = attemptsLeft => {
      const el = brandsRef.current;
      if (el) {
        const offset = 90;
        const elementPosition = el.getBoundingClientRect().top + window.scrollY;
        const offsetPosition = elementPosition - offset;

        window.scrollTo({
          top: offsetPosition,
          behavior: 'smooth',
        });
        return;
      }

      if (attemptsLeft <= 0) return;
      if (typeof window !== 'undefined') {
        window.requestAnimationFrame(() => tryScroll(attemptsLeft - 1));
      }
    };

    tryScroll(20);
  }, []);
  // if (allInProgress) {
  //   return (
  //     <div className={css.loaderContainer}>
  //       <Loader />
  //     </div>
  //   );
  // }

  return (
    <Page
      schema={{
        '@context': 'http://schema.org',
        '@type': 'WebPage',
        name: schemaTitle,
        description: intl.formatMessage({ id: 'LandingPage.metaDescription' }),
      }}
      title={schemaTitle}
      description={intl.formatMessage({
        id: 'LandingPage.metaDescription',
      })}
      className={css.page}
    >
      <TopbarContainer />
      <div className={css.mainContainer} role="main">
        <SectionContainer className={css.section1}>
          <div className={css.redirectCont}>
            <ul>
              <li onClick={() => handleCategoryClick('women')}>
                <FormattedMessage id="LandingPage.headListWomen" />
              </li>
              <li onClick={() => handleCategoryClick('men')}>
                <FormattedMessage id="LandingPage.headListMen" />
              </li>
              <li onClick={scrollToBrands}>
                <FormattedMessage id="LandingPage.headListBrands" />
              </li>
              {/* <li>
                <FormattedMessage id="LandingPage.headListSale" />
              </li> */}
            </ul>
          </div>
          <div className={classNames(css.header, css.section)}>
            <DesignedImagerWrapper
              firstBtn={'LandingPage.headerctaButtonSell'}
              secondBtn={'LandingPage.headerctaButtonShop'}
              onSubmitFirst={handleRedirect}
              onSubmitSecond={handleRedirect}
              intl={intl}
              heroData={heroData}
            />
          </div>
          <H4 as="h1" className={css.h1}>
            {/* {intl.formatMessage({ id: 'LandingPage.heading' })} */}
            {description}
          </H4>
          <div className={css.ctaContainer}>
            <PrimaryButton
              onClick={() => handleRedirect('CreateListingPage')}
              className={classNames(css.headingBtn, css.secondHeadingBtn)}
            >
              {intl.formatMessage({ id: 'LandingPage.headerctaButtonSell' })}
            </PrimaryButton>
          </div>
        </SectionContainer>
        <SectionContainer className={classNames(css.hotSection, css.section1)}>
          <div className={css.hotSectionHeader}>
            <H4 className={css.h2} as="h2">
              {intl.formatMessage({ id: 'LandingPage.sectionHeadingHot' })}
            </H4>
            <ShowMoreRedirectSearchButton params={{ meta_trending: true }} />
          </div>
          <div ref={hotRef}>
            {showHot ? (
              listings.length === 0 ? (
                <Empty textId={'LandingPage.emptyHotLists'} />
              ) : (
                <div className="slider-container">
                  <Carousel
                    className={
                      css.sliderForListingCards
                      // window.innerWidth >= 768 && css.desktopArrows
                    }
                    {...settings}
                  >
                    {listings.map(listing => {
                      const isLiked = favroiteListingIds && favroiteListingIds.has(listing.id.uuid);
                      return (
                        <div key={listing?.id?.uuid} className={css.listingCard}>
                          <ListingCard
                            // showListingName={false}
                            rootClassName={classNames(css.indivCard, css.reduceShadow)}
                            history={history}
                            listing={listing}
                            isLiked={isLiked}
                            showAuthorInfo={false}
                            showListingName={false}
                            showRating={false}
                            onAddList={onAddList}
                          />
                        </div>
                      );
                    })}
                  </Carousel>
                </div>
              )
            ) : (
              <div style={{ minHeight: 260 }} />
            )}
          </div>
        </SectionContainer>

        <RenderWhenVisible rootMargin="600px" minHeight={420}>
          <SectionContainer className={css.hotSection}>
            <div className={css.hotSectionHeader}>
              <H4 className={css.h2} as="h2">
                {intl.formatMessage({ id: 'LandingPage.sectionHeadingPriceUnder' })}
              </H4>
              <ShowMoreRedirectSearchButton params={{ price: '0,700' }} />
            </div>
            {filteredListings.length === 0 ? (
              <Empty textId={'LandingPage.underFilterLists'} />
            ) : (
              <div className="slider-container">
                <Carousel className={css.sliderForListingCards} {...settings}>
                  {filteredListings.map(listing => {
                    const isLiked = favroiteListingIds && favroiteListingIds.has(listing.id.uuid);

                    return (
                      <div key={listing?.id?.uuid} className={css.listingCard}>
                        <ListingCard
                          // showListingName={false}
                          className={classNames(css.indivCard, css.reduceShadow)}
                          history={history}
                          isLiked={isLiked}
                          listing={listing}
                          showAuthorInfo={false}
                          showListingName={false}
                          showRating={false}
                          onAddList={onAddList}
                        />
                      </div>
                    );
                  })}
                </Carousel>
              </div>
            )}
          </SectionContainer>
        </RenderWhenVisible>

        <RenderWhenVisible rootMargin="800px" disabled={forceBrandsVisible}>
          <SectionContainer className={css.hotSection}>
            <div className={css.brandRef} ref={brandsRef}>
              <div className={css.hotSectionHeader}>
                <H4 className={css.h2} as="h2">
                  {intl.formatMessage({ id: 'LandingPage.sectionHeadingBrands' })}
                </H4>
                <ShowMoreRedirectSearchButton extra={{ hash: 'brands' }} />
              </div>
            </div>
            {topBrands.length === 0 ? (
              <Empty textId={'LandingPage.noBrandList'} />
            ) : (
              <div className="slider-container">
                <Carousel className={css.sliderForBrandCards} {...brandsSettings}>
                  {topBrands.map((e, i) => (
                    <div
                      key={e._id}
                      onClick={() => handleBrandClick(e.value)}
                      className={css.brandsName}
                    >
                      <img src={e.image?.url} loading="lazy" alt={e.label} />
                      <h4 className={css.h4lineheight} as="h4">
                        {e.label.toUpperCase()}
                      </h4>
                    </div>
                  ))}
                </Carousel>
              </div>
            )}
          </SectionContainer>
        </RenderWhenVisible>

        <RenderWhenVisible rootMargin="800px" minHeight={320}>
          <SectionContainer className={classNames(css.hotSection, css.extraPadding)}>
            <H4 className={css.h2} as="h2">
              {intl.formatMessage({ id: 'LandingPage.sectionHeadingShopByVibe' })}
            </H4>
            <div className="slider-container">
              <Carousel className={css.vibeSlider} {...vibeSettings}>
                {SHOP_BY_VIBE.map((e, i) => {
                  return (
                    <div
                      onClick={() => handleVibeClick(e.paramKey)}
                      key={i}
                      className={css.vibeCard}
                    >
                      <img src={e.img} loading="lazy" alt={e.alt} />
                      <H4 as="h4" className={css.paddingH4}>
                        {e.title.toUpperCase()}
                      </H4>
                    </div>
                  );
                })}
              </Carousel>
            </div>
          </SectionContainer>
        </RenderWhenVisible>

        <RenderWhenVisible rootMargin="900px" minHeight={320}>
          <SectionContainer className={css.hotSection}>
            <H4 className={classNames(css.h2, css.h2padleft)} as="h2">
              {intl.formatMessage({ id: 'LandingPage.sectionHeadingKlubsPick' })}
            </H4>
            {listings.length === 0 ? (
              <Empty textId={'LandingPage.klubPicksLists'} />
            ) : (
              <div className="slider-container" style={{ maxHeight: '230px' }}>
                <Carousel
                  className={classNames(css.klubPickSlider, css.sliderForKlubCards, {
                    [css.lessContent]: klubPicks.length < 4,
                  })}
                  {...klubPicksSettings}
                >
                  {klubPicks.map(user => {
                    const { id, publicData, stats, profile } = user;
                    const urlParams = publicData.profileUrl
                      ? {
                          name: 'ProfilePageByUsername',
                          params: { userName: publicData.profileUrl },
                        }
                      : { name: 'ProfilePage', params: { id: id.uuid } };
                    return (
                      <NamedLink key={id.uuid} {...urlParams} className={css.klubPicksCard}>
                        <div>
                          <div className={css.headerDiv}>
                            <AvatarMedium disableProfileLink user={user} />
                            <div className={css.stats}>
                              <p>
                                <strong>{stats.totalListings}</strong>
                                <FormattedMessage id="ProfilePage.headingStatListings" />
                              </p>
                              <p>
                                <strong>{stats.likesCount}</strong>{' '}
                                <FormattedMessage id="ProfilePage.headingStatLikes" />
                              </p>
                              <p>
                                <strong>{stats.followCount}</strong>
                                <FormattedMessage id="ProfilePage.headingStatFollowers" />
                              </p>
                            </div>
                          </div>
                          <div className={css.secondaryHead}>
                            <H4 as="h3" className={classNames(css.profUrl, css.h3)}>
                              {publicData?.rawProfileUrl?.toUpperCase() ||
                                publicData?.profileUrl?.toUpperCase()}
                            </H4>
                            {profile.metadata?.rating && profile.metadata?.ratingCount && (
                              <div className={css.rating}>
                                <span className={css.ratingValue}>
                                  <span>{profile.metadata?.rating ?? 0}</span>
                                  <FaStar />
                                </span>
                                <span className={css.ratingCount}>
                                  ({profile.metadata?.ratingCount ?? 0})
                                </span>
                              </div>
                            )}
                          </div>
                          <p dangerouslySetInnerHTML={{ __html: profile.bio }}></p>
                        </div>
                      </NamedLink>
                    );
                  })}
                </Carousel>
              </div>
            )}
          </SectionContainer>
        </RenderWhenVisible>

        <RenderWhenVisible rootMargin="900px" minHeight={320}>
          <SectionContainer>
            <H4 className={css.h2} as="h2">
              {intl.formatMessage({ id: 'LandingPage.sectionHeadingWhyKloset' })}
            </H4>
            <div className={css.whyKloset}>
              {WHY_KLOSET_ICONS.map((_, i) => (
                <div className={classNames(css.card, css.cardheight)} key={i}>
                  <H4 className={css.h3} as="h3">
                    {intl.formatMessage({ id: `LandingPage.whyKlosetTitle${i + 1}` })}
                  </H4>
                  <p>{intl.formatMessage({ id: `LandingPage.whyKlosetDesc${i + 1}` })}</p>
                  <div className={css.iconsCont}>
                    <img src={_} alt="" loading="lazy" />
                  </div>
                </div>
              ))}
            </div>
          </SectionContainer>
        </RenderWhenVisible>

        <RenderWhenVisible rootMargin="900px" minHeight={300}>
          <SectionContainer className={classNames(css.hotSection, css.reviewSection)}>
            <H4 className={classNames(css.h2, css.h2padleft)} as="h2">
              {intl.formatMessage({ id: 'LandingPage.sectionHeadingKlubReviews' })}
            </H4>
            <div className="slider-container">
              <Carousel {...reviewSettings}>
                {testimonialData.map((e, i) => (
                  <div key={i} className={css.testCard}>
                    <p className={classNames(css.title, css.title2)}>
                      <span>{e.title}</span>
                      <span>
                        {Array.from({ length: 5 }, (_, i) => (
                          <span
                            key={i}
                            className={classNames(css.star, {
                              [css.starFilled]: i + 1 <= (e.rating ?? 0),
                            })}
                          >
                            <FaStar />
                          </span>
                        ))}
                      </span>
                    </p>
                    <p className={css.review}>{`"${e.review}"`}</p>
                    <H4 className={css.h5} as="h5">{`- ${e.author}`}</H4>
                  </div>
                ))}
              </Carousel>
            </div>
          </SectionContainer>
        </RenderWhenVisible>

        <RenderWhenVisible rootMargin="900px" minHeight={320}>
          <SectionContainer>
            <H4 className={css.h2} as="h2">
              {intl.formatMessage({ id: 'LandingPage.sectionHeadingHowWorks' })}
            </H4>
            <div className={css.howWorks}>
              {HOW_IT_WORKS_ICONS.map((_, i) => (
                <div key={i} className={css.card}>
                  <div className={css.cardHeader}>
                    <H4 className={css.h3} as="h3">
                      {intl.formatMessage({ id: `LandingPage.howToTitle${i + 1}` })}
                    </H4>
                    <div className={css.imgCont}>
                      <img className={css[`img${i + 1}`]} src={_} alt="" loading="lazy" />
                    </div>
                  </div>
                  <p>
                    {intl.formatMessage({ id: `LandingPage.howToDesc${i + 1}` })}
                    {i + 1 === 1 && (
                      <span>{intl.formatMessage({ id: `LandingPage.howToHighlight1` })}</span>
                    )}
                  </p>
                </div>
              ))}
            </div>
          </SectionContainer>
        </RenderWhenVisible>
        {/* <SectionContainer className={css.section}>
          <H4 className={css.h2} as="h2">
            {intl.formatMessage({ id: 'LandingPage.sectionHeadingGetApp' })}
          </H4>
          <div className={classNames(css.header, css.section)}>
            <DesignedImagerWrapper
              img={
                'https://sharetribe-assets.imgix.net/682b2a43-8956-498d-a33e-423c315917ca/raw/b8/2e372aafdbfec627c8e9e04d3912dedff7ce58?auto=format&fit=clip&h=2400&w=2400&s=794c490604c51f1e9f5517c1253db8dd'
              }
              className={css.imageWrapper}
              onSubmitFirst={() => console.log('android click')}
              onSubmitSecond={() => console.log('apple click')}
              intl={intl}
              title={'LandingPage.finalSectionHeading'}
              firstBtn={'LandingPage.androidAppCtaBtnText'}
              secondBtn={'LandingPage.appleAppCtaBtnText'}
            />
          </div>
        </SectionContainer> */}
      </div>
      <FooterComponent />
    </Page>
  );
};

LandingPageComponent.propTypes = {
  pageAssetsData: object,
  inProgress: bool,
  error: propTypes.error,
};

const mapStateToProps = state => {
  const { pageAssetsData, inProgress, error } = state.hostedAssets || {};
  const {
    hotListings,
    underFilterListings,
    topBrands,
    klubPicks,
    allInProgress,
  } = state.LandingPage;
  const { favroiteListingIds } = state.SearchPage;
  return {
    pageAssetsData,
    inProgress,
    error,
    listings: hotListings,
    filteredListings: underFilterListings,
    topBrands,
    klubPicks,
    favroiteListingIds,
    allInProgress,
  };
};

const mapDispatchToProps = dispatch => ({
  onAddList: (listingId, authorId) => dispatch(addToLikeList(listingId, authorId)),
});

// Note: it is important that the withRouter HOC is **outside** the
// connect HOC, otherwise React Router won't rerender any Route
// components since connect implements a shouldComponentUpdate
// lifecycle hook.
//
// See: https://github.com/ReactTraining/react-router/issues/4671
const LandingPage = compose(
  withRouter,
  connect(mapStateToProps, mapDispatchToProps)
)(LandingPageComponent);

export default LandingPage;
